/**
 * `gac scan` upload contract — V108-5-4 / ADR-0087 §4, ADR-0086 §2 ("Local (`gac scan`)").
 *
 * The customer runs the infrastructure scanner (`scanInfraRepo`, `full` mode) on their own
 * checkout and uploads ONLY the extracted facts: dependency / SDK names, IaC resource types,
 * hostnames, env var NAMES, CI config paths, with repository-relative file paths and line numbers.
 * Never source bytes. `POST /api/ci/systems/:systemId/scan-results` validates the body with this
 * function before recording it with `customer_reported` provenance (migration 621).
 *
 * PURE and dependency-free (no zod), so the CLI validates with the same code before it uploads.
 * Strict: unknown keys anywhere are rejected (a body naming a workspace is refused, the workspace
 * comes only from the token). Values that look like a credential or an e-mail address are refused:
 * a scan result never needs one, and the server must not store one.
 */
import { INFRA_FUNCTIONS, INFRA_SCAN_MODES } from './infra-repo-scanner.js';
import { containsSecretOrEmail } from '../utils/secret-filter.js';
import { productDisplayName } from '../connectors/product-clues.js';
export const SCAN_RESULTS_SCHEMA = 'gacpro/scan-results@1';
/** ADR-0086 Security: uploads are capped at 5 MiB (the route answers 413 above it). */
export const SCAN_RESULTS_MAX_BYTES = 5 * 1024 * 1024;
/** Each item is several inventory writes; 500 products is far above any real repository (the provider registry is smaller). */
export const SCAN_RESULTS_MAX_ITEMS = 500;
const MAX_HITS = 50;
const MAX_CALL_SITES = 200;
const CLUE_KINDS = new Set(['sdk', 'wrangler_binding', 'wrangler_config', 'iac_provider', 'iac_resource', 'env_var', 'domain', 'ci_config']);
/**
 * V108-5-5 (L-7): a hit's value has the shape the scanner produces for its kind and nothing else, so
 * free text never reaches the inventory (or the classifier that later reads it).
 */
export const CLUE_VALUE_PATTERNS = {
    // npm:<pkg> / npm:@scope/<pkg>, pypi:<name>, golang:<module path>
    sdk: /^(npm:(@[a-z0-9][a-z0-9._~-]{0,100}\/)?[a-z0-9][a-z0-9._~-]{0,213}|pypi:[a-z0-9][a-z0-9._-]{0,99}|golang:[a-z0-9][a-z0-9._~\/-]{0,199})$/i,
    // a wrangler binding table name (`d1_databases`, `ai`, …)
    wrangler_binding: /^[a-z][a-z0-9_]{0,63}$/,
    wrangler_config: /^services$/,
    // a Terraform provider source (`hashicorp/aws`)
    iac_provider: /^[a-z0-9][a-z0-9-]{0,63}\/[a-z0-9_][a-z0-9_-]{0,63}$/,
    // a Terraform resource type, `aws:<svc>:<type>`, `azure:microsoft.<ns>/<type>`, or a container image name
    iac_resource: /^([a-z0-9_]{1,100}|aws:[a-z0-9]{1,64}:[a-z0-9]{1,64}|azure:microsoft\.[a-z]{1,64}(\/[a-z]{1,64}){1,4}|[a-z0-9][a-z0-9._\/-]{0,199})$/,
    // an environment variable NAME
    env_var: /^[A-Z][A-Z0-9_]{2,80}$/,
    // a host name
    domain: /^(?=.{1,253}$)([a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9-]{1,63}$/,
    // a repository-relative CI config path, lower case
    ci_config: /^[a-z0-9._][a-z0-9._\/-]{0,299}$/,
};
const FUNCTIONS = new Set(INFRA_FUNCTIONS);
const MODES = new Set(INFRA_SCAN_MODES);
const TARGET = /^[A-Za-z0-9][A-Za-z0-9._\/-]{0,299}$/;
const PROVIDER = /^[a-z0-9][a-z0-9-]{0,63}$/;
const PRODUCT = /^(\*|[a-z0-9][a-z0-9._-]{0,99})$/;
const isObj = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const isInt = (v, max = 1_000_000_000) => typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= max;
const isConf = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0 && v <= 1;
/** A repository-relative path: no drive letter, no leading slash, no `..` segment. */
export function isRepoRelativePath(p) {
    if (typeof p !== 'string' || !p || p.length > 500 || /[\0\r\n]/.test(p))
        return false;
    if (p.startsWith('/') || p.startsWith('\\') || /^[A-Za-z]:/.test(p))
        return false;
    return !p.split(/[\\/]/).includes('..');
}
export function validateScanResultsUpload(body) {
    const errors = [];
    const err = (path, message) => { if (errors.length < 50)
        errors.push({ path, message }); };
    const onlyKeys = (o, allowed, at) => {
        for (const k of Object.keys(o))
            if (!allowed.includes(k))
                err(at ? `${at}.${k}` : k, 'unknown field');
    };
    const text = (v, at, max, re) => {
        if (typeof v !== 'string' || !v || v.length > max || /[\0\r\n]/.test(v)) {
            err(at, `must be a single-line string of 1..${max} characters`);
            return false;
        }
        if (re && !re.test(v)) {
            err(at, 'has an invalid format');
            return false;
        }
        if (containsSecretOrEmail(v)) {
            err(at, 'looks like a credential or an e-mail address; scan results never carry one');
            return false;
        }
        return true;
    };
    if (!isObj(body))
        return { ok: false, errors: [{ path: '', message: 'body must be a JSON object' }] };
    onlyKeys(body, ['schema', 'target', 'source_commit', 'complete', 'result'], '');
    if (body.schema !== SCAN_RESULTS_SCHEMA)
        err('schema', `must be ${SCAN_RESULTS_SCHEMA}`);
    text(body.target, 'target', 300, TARGET);
    if (body.source_commit !== null && !(typeof body.source_commit === 'string' && /^[0-9a-f]{7,64}$/i.test(body.source_commit)))
        err('source_commit', 'must be a commit sha or null');
    if (typeof body.complete !== 'boolean')
        err('complete', 'must be a boolean');
    const r = body.result;
    if (!isObj(r)) {
        err('result', 'must be an object');
    }
    else {
        onlyKeys(r, ['parser_version', 'mode', 'files_scanned', 'files_skipped', 'items', 'hits_found'], 'result');
        text(r.parser_version, 'result.parser_version', 40, /^infra-repo-scan@\d+$/);
        if (typeof r.mode !== 'string' || !MODES.has(r.mode))
            err('result.mode', 'must be a scan mode');
        for (const k of ['files_scanned', 'files_skipped', 'hits_found'])
            if (!isInt(r[k]))
                err(`result.${k}`, 'must be a non-negative integer');
        if (!Array.isArray(r.items) || r.items.length > SCAN_RESULTS_MAX_ITEMS) {
            err('result.items', `must be an array of at most ${SCAN_RESULTS_MAX_ITEMS} items`);
        }
        else {
            r.items.forEach((it, i) => {
                const at = `result.items.${i}`;
                if (!isObj(it)) {
                    err(at, 'must be an object');
                    return;
                }
                onlyKeys(it, ['ref', 'provider_key', 'product', 'name', 'suggested_function', 'confidence', 'clue_kinds', 'hits', 'call_sites'], at);
                text(it.provider_key, `${at}.provider_key`, 64, PROVIDER);
                text(it.product, `${at}.product`, 100, PRODUCT);
                if (it.ref !== `${String(it.provider_key)}/${String(it.product)}`)
                    err(`${at}.ref`, 'must be provider_key/product');
                // L-7: the display name is the registry's, never free text.
                if (text(it.name, `${at}.name`, 200) && typeof it.provider_key === 'string' && typeof it.product === 'string'
                    && it.name !== productDisplayName({ provider_key: it.provider_key, product_id: it.product }))
                    err(`${at}.name`, "must be the product's registry name");
                if (typeof it.suggested_function !== 'string' || !FUNCTIONS.has(it.suggested_function))
                    err(`${at}.suggested_function`, 'must be an infrastructure function');
                if (!isConf(it.confidence))
                    err(`${at}.confidence`, 'must be between 0 and 1');
                if (!Array.isArray(it.clue_kinds) || it.clue_kinds.some((k) => typeof k !== 'string' || !CLUE_KINDS.has(k)))
                    err(`${at}.clue_kinds`, 'must list clue kinds');
                if (!Array.isArray(it.hits) || it.hits.length > MAX_HITS) {
                    err(`${at}.hits`, `must be an array of at most ${MAX_HITS}`);
                }
                else {
                    it.hits.forEach((h, j) => {
                        const ha = `${at}.hits.${j}`;
                        if (!isObj(h)) {
                            err(ha, 'must be an object');
                            return;
                        }
                        onlyKeys(h, ['kind', 'value', 'path', 'line', 'confidence'], ha);
                        if (typeof h.kind !== 'string' || !CLUE_KINDS.has(h.kind))
                            err(`${ha}.kind`, 'must be a clue kind');
                        else
                            text(h.value, `${ha}.value`, 300, CLUE_VALUE_PATTERNS[h.kind]);
                        if (!isRepoRelativePath(h.path))
                            err(`${ha}.path`, 'must be a repository-relative path');
                        if (!isInt(h.line) || h.line < 1)
                            err(`${ha}.line`, 'must be a line number');
                        if (!isConf(h.confidence))
                            err(`${ha}.confidence`, 'must be between 0 and 1');
                    });
                }
                if (!Array.isArray(it.call_sites) || it.call_sites.length > MAX_CALL_SITES) {
                    err(`${at}.call_sites`, `must be an array of at most ${MAX_CALL_SITES}`);
                }
                else {
                    it.call_sites.forEach((s, j) => {
                        const sa = `${at}.call_sites.${j}`;
                        if (!isObj(s)) {
                            err(sa, 'must be an object');
                            return;
                        }
                        onlyKeys(s, ['path', 'line'], sa);
                        if (!isRepoRelativePath(s.path))
                            err(`${sa}.path`, 'must be a repository-relative path');
                        if (!isInt(s.line) || s.line < 1)
                            err(`${sa}.line`, 'must be a line number');
                    });
                }
            });
        }
    }
    return errors.length ? { ok: false, errors } : { ok: true, value: body };
}
//# sourceMappingURL=scan-results-upload.js.map